/* @flow */
/* eslint-env es6,browser */

//set global variable needed for AOL(adtech) library
var _bidRequestConfig = window.bidRequestConfig || {};
var _dacBidRequestConfigs = window.dacBidRequestConfigs || [];
var _ADTECH = window.ADTECH || {};
window.bidRequestConfig = _bidRequestConfig;
window.dacBidRequestConfigs = _dacBidRequestConfigs;
window.ADTECH = _ADTECH;

import * as u from '../utils';

/**
 * @description Implements functionality for adtech header bidding
 * @exports bidders/adtechbid
 * @requires {@link module:utils}
 */
class _adtechbid {
  _name: string;
  _bInited: 1 | 0 | -1;
  _first: boolean;
  _DefConfig: adtechConfig;
  _config: adtechConfig;
  _excludeList: Array<string>;
  _bidResponses: Object;

  //Member variable default initialization in constructor
  constructor(){
    this._name = 'adtech';
    this._bInited = u.F_NOT_INITED;
    this._first = true;
    this._DefConfig = u.extend(true, this._DefConfig, {
      positions: [],
      exclude: '',
      bidKey : 'aolbid',
      timeout : 2000,
      qpKey : 'ATEC_BID', //not used currently, only qpKey per position is used
      cVar : 'ATEC_VAR',
      initTimeout : 1000,
      script: '',
      id: '',
      posTokenMap: {},
      server : ''
    });

    /**
     * @global
     * @typedef {Object} adtechConfig
     * @property {Array<string>} positions
     * @property {string} [exclude]
     * @property {string} bidKey
     * @property {number} [timeout=2000]
     * @property {string} [cVar=ATEC_VAR]
     * @property {Object} posTokenMap
     * @property {string} script
     * @property {string} id
     * @property {string} server
     * @property {number} [initTimeout=1000]
     */
    this._config = u.extend(true, this._DefConfig);
    this._excludeList = [];
    this._bidResponses = {};
  }

  /**
   * get list of bid positions supported by this header bidder
   * @param {string} pName
   * @return {Array<string>}
   */
  getBidPositions(pName: string) {
    var ret = this._config.positions;
    if(u.isBrowserExcluded(pName, this._excludeList)) {
      ret = [];
    }
    return ret;
  }

  /**
   * Reset & Update config specific to this bidder
   * @param {adtechConfig} oConfig
   */
  updateConfig(oConfig: adtechConfig) {
    this._config = u.extend(true, {}, this._DefConfig, oConfig);
    //compute exclusion list again
    this._excludeList = u.getBrowserExcludeList(this._config.exclude);
  }

  /**
   * getter for qpkey
   * @param {string} pos
   */
  getQPKey(pos: string) {
    return u.isObject(this._config.posTokenMap[pos]) ? this._config.posTokenMap[pos].qpKey : '';
  }

  /**
   * getter for posKey
   * @param {string} pos
   */
  getPosKey(pos: string) {
    return u.isObject(this._config.posTokenMap[pos]) ? this._config.posTokenMap[pos].alias : '';
  }

  /**
   * getter for cVar
   * @param {string} pos
   */
  getCVar() {
    return this._config.cVar;
  }

  /**
   * We need to init/load AOL JS library only once asynchronously
   * AOL doesn't provide any callback onload. We will wait for initTimeout and
   * check if script is loaded (by checking AOL defined fn)
   * During next invocation  if the script is not loaded , we consider
   * script loading failed. Ad blocking/AOL server issue? We should silently skip header
   * bidding in this case
   * @access private
   */
  _init(/**posList */) {

    //returns promise
    return new Promise((resolve, reject) => {
      if(this._bInited === u.F_INITED) {
        if(u.isObject(_ADTECH) && u.isFunction(_ADTECH.loadPubAd)) {
          resolve();
          return;
        }
        this._bInited = u.F_FAILED;
        reject();
        return;
      }
      if(this._bInited === u.F_FAILED) {
        reject();
        return;
      }
      //Let's load the config for the first time
      //Let's do a basic santiy check (script shdn't be empty etc.,)
      if(!this._config.script.length || !this._config.id.length ||
         !this._config.server.length) {
        u.logError(`${this._name} config missing mandatory data`);
        reject();
        return;
      }

      var misPos = this._config.positions.filter(pos => !this._config.posTokenMap[pos]);
      if (misPos.length) {
        u.logError(`${this._name} config positions missing in posTokenMap ${misPos.join(',')}`);
        reject();
        return;
      }

      //configure and load AOL script
      u.extend(true, _bidRequestConfig, {
        server: this._config.server,
        network: this._config.id,
        bidKey: this._config.bidKey,
        bidResponseTimeout: this._config.timeout
      });

      this._config.positions.forEach(v => {
          _dacBidRequestConfigs.push({
            placement: this._config.posTokenMap[v].placement,
            alias: this.getPosKey(v),
            adContainerId: this._config.posTokenMap[v].adContainerId,
            bidfloor: '0.1',
            secure: true
          });
      });

      u.loadJSWithTimeout(this._config.script, document, true, this._config.initTimeout,
                         { context: window.ADTECH, func: 'loadPubAd' }).then(() => resolve(),
                                                                             () => reject());
      this._bInited = u.F_INITED;
    });
  }

  /**
   * - AOL response object for each pos is stored in bidResponses object
   * - Response object has success which indicates whether bid is successful or not
   * - and alias, fixedCpm values are available in the top level resp object
   * @access private
   */
  _processBidResponse(posList: Array<string>, ret: hbPartnerData) {
    var alias = '', res = {};
    this._first = false;
    posList.forEach(pos => {
      alias = this.getPosKey(pos),
      res = this._bidResponses[alias];
      if (res && res.success) {
        u.logDebug(`${this._name} alias:${alias} fixedCpm:${res.fixedCpm}`);
        ret[pos].info = `${alias}|${res.fixedCpm}`;

        //query param string to pass to OpenX ad call (cpm value is in dollars)
        ret[pos]._QP[this.getQPKey(pos)] = res.fixedCpm;
        ret[pos]._CV[this._config.cVar] = alias;
      }
    });
    return ret;
  }

  _getAds(posList: Array<string>, ret: hbPartnerData) {
    this._bidResponses = {};
    var bidPromise = new Promise(resolve => {
      // bid response callback
      _bidRequestConfig.onBidResponse = response => {
        this._bidResponses[response.alias] = response;
        if (posList.length === Object.keys(this._bidResponses).length) {
          resolve();
        }
      };
      //call loadPubAd for each position
      const loadAd = (this._first) ? _ADTECH.loadPubAd : _ADTECH.refreshPubAd;
      posList.map(v => this.getPosKey(v))
             .forEach(loadAd);
    });

    return new Promise(resolve => {
      Promise.race([bidPromise, u.getTimeoutPromise(this._config.timeout, this._name)]).then( () => {
        resolve(this._processBidResponse(posList, ret));
      }, msg => {
        u.logError(msg);
        resolve(this._processBidResponse(posList, ret));
      });
    });
  }

  /**
   * get bids for current partner
   * @param {Array<string>} posList - list of ad positions
   * @param {Object.<string, hbPosData>} ret - header bidding default response
   * @return {Promise<Object.<string, hbPosData>>}
   */
  getBids(posList: Array<string>, ret: hbPartnerData) {
    return this._init(posList).then(() => {
      //get bids
      u.logDebug(`${this._name} Init is done. getting ad bids`);
      return this._getAds(posList, ret);
    }, () => {
      u.logError(`${this._name} init failed`);
      return ret;
    });
  }

 /**
  * render ad for this partner
  * @param {window} win - window object
  * @param {window.document} doc - document object
  * @param {string} pos - ad position
  * @param {string} alias - alias specific to this position
  */
  renderAd(win: window, doc: window.document, pos: string, alias: string) {
    try {
      if (this._bidResponses[alias]) {
        //accessing ad creative from api response stored in .api.data
        var adm = this._bidResponses[alias].api.data.seatbid[0].bid[0].adm;
        doc.write(adm);
      }
    } catch(e) {
      u.logError(`${this._name} render ad failure ${e.message}`);
    }
  }
}

//export singleton object as default for this module
export default new _adtechbid();
